import { InvoiceSource, InvoiceStatus } from "../types/invoice";

/**
 * Optional context for transition checks that depend on invoice state
 * beyond the status enum itself.
 */
export interface InvoiceStatusTransitionContext {
    /**
     * True when any line has matchedAmount > 0. Used to block:
     * - manual Paid → Open, since the invoice was settled via matched
     *   transactions (reopen must happen by unmatching instead).
     * - any transition to Void, since matches must be removed before an
     *   invoice can be voided.
     */
    hasMatches?: boolean;
    /**
     * Whether the invoice has (or will have, for the same update) a
     * counterparty assigned. Only DRAFT invoices are allowed to have no
     * counterparty — leaving DRAFT for any other status requires one.
     * Undefined means "unknown / not checked" and never blocks the
     * transition, so callers that don't track counterparty state (e.g. the
     * frontend enable/disable check before the invoice is loaded) keep
     * their prior behavior.
     */
    hasCounterparty?: boolean;
    /**
     * Internal, backend-only context set exclusively after the service has
     * verified that a Void is part of a revision flow (the Void is paired with
     * a validated revision Draft that links back to this invoice). When true,
     * the `hasMatches` block on Void → is skipped, so an Open invoice with
     * transaction matches can be voided as part of revising it. This is never
     * derived from client input — see the `revisionInvoiceId` handling in the
     * invoice service — so the ordinary status dropdown (which never sets it)
     * keeps the normal "matches block Void" behavior.
     */
    isRevision?: boolean;
}

// DRAFT is only an entry state: once an invoice is finalized (OPEN / PAID) it
// can never return to DRAFT. No status lists DRAFT as a target — reverting a
// finalized invoice to DRAFT is intentionally unsupported (correct it by
// voiding and re-issuing instead). UNCOLLECTABLE likewise cannot go back to
// OPEN: recovery flows through PAID. These omissions are deliberate; do not
// re-add DRAFT or UNCOLLECTABLE → OPEN edges without revisiting the JE
// regeneration and matching side effects that assume them.
const ALLOWED_TRANSITIONS: Record<InvoiceStatus, readonly InvoiceStatus[]> = {
    [InvoiceStatus.DRAFT]: [InvoiceStatus.OPEN],
    [InvoiceStatus.OPEN]: [
        InvoiceStatus.PAID,
        InvoiceStatus.VOID,
        InvoiceStatus.UNCOLLECTABLE,
    ],
    [InvoiceStatus.PAID]: [InvoiceStatus.OPEN],
    [InvoiceStatus.UNCOLLECTABLE]: [InvoiceStatus.PAID, InvoiceStatus.VOID],
    [InvoiceStatus.VOID]: [],
};

export const canTransitionInvoiceStatus = (
    from: InvoiceStatus,
    to: InvoiceStatus,
    ctx: InvoiceStatusTransitionContext = {},
): boolean => {
    if (from === to) {
        return true;
    }

    if (!ALLOWED_TRANSITIONS[from].includes(to)) {
        return false;
    }

    if (
        from === InvoiceStatus.PAID &&
        to === InvoiceStatus.OPEN &&
        ctx.hasMatches === true
    ) {
        return false;
    }

    if (
        to === InvoiceStatus.VOID &&
        ctx.hasMatches === true &&
        ctx.isRevision !== true
    ) {
        return false;
    }

    if (
        from === InvoiceStatus.DRAFT &&
        to === InvoiceStatus.OPEN &&
        ctx.hasCounterparty === false
    ) {
        return false;
    }

    return true;
};

// Deletion is available while an invoice has not been issued. Once it is
// open or later, the record stays for the audit trail.
// System sync flows bypass this user-facing rule at the service layer.
export const canDeleteInvoice = (status: InvoiceStatus): boolean =>
    status === InvoiceStatus.DRAFT;

const DELETABLE_INVOICE_SOURCES: readonly InvoiceSource[] = [
    InvoiceSource.MANUAL,
    InvoiceSource.IMPORTER,
    InvoiceSource.MCP,
];

export const isDeletableInvoiceSource = (source: InvoiceSource): boolean =>
    DELETABLE_INVOICE_SOURCES.includes(source);

export const isDeletableInvoice = (invoice: {
    source: InvoiceSource;
    status: InvoiceStatus;
}): boolean =>
    isDeletableInvoiceSource(invoice.source) &&
    canDeleteInvoice(invoice.status);

export const invoiceHasMatches = (
    lines: ReadonlyArray<{ matchedAmount: number }> | null | undefined,
): boolean => (lines ?? []).some((line) => line.matchedAmount > 0);
